import type { WallBoard, WallId, WallTheme } from '@walls/store/types';
import type { WallsClient } from './client';

export interface WallAuthenticationStatus {
  requiresAuthentication: boolean;
  board?: {
    id: WallId;
    title: string;
    isPublic: boolean;
  };
}

export interface SaveWallTemplateInput {
  name: string;
  description: string;
}

export class BoardApi {
  constructor(private readonly client: WallsClient) {}

  getBoard = (boardId: WallId, key?: string | null) => {
    return this.client.requestJson<WallBoard>(`/boards/${boardId}`, boardId, key);
  };

  getBoardRequiresAuthentication = (
    action: 'topic' | 'join',
    boardId: WallId,
    key?: string | null
  ) => {
    return this.client.requestJson<WallAuthenticationStatus>(
      `/boards/password/${action}/${boardId}`,
      boardId,
      key
    );
  };

  authenticateBoard = (boardId: WallId, password: string, key?: string | null) => {
    return this.client.requestJson<{ key?: string }>(
      `/boards/authenticate/${boardId}`,
      boardId,
      key,
      {
        method: 'POST',
        body: JSON.stringify({ password }),
      }
    );
  };

  sendInvitations = (
    boardId: WallId,
    emailAddresses: string,
    emailMessage: string,
    key?: string | null
  ) => {
    return this.client.requestJson<void>(`/boards/invite/${boardId}`, boardId, key, {
      method: 'POST',
      body: JSON.stringify({ emailAddresses, emailMessage }),
    });
  };

  sendFeedback = (comment: string) => {
    return this.client.requestJson<void>('/sendFeedback', undefined, undefined, {
      method: 'POST',
      body: JSON.stringify({ source: 'wall', comment }),
    });
  };

  saveWallTemplate = (boardId: WallId, input: SaveWallTemplateInput, key?: string | null) => {
    return this.client.requestJson<void>(`/templates/from-wall/${boardId}`, boardId, key, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  };

  getWallTheme = (boardId: WallId, key?: string | null) => {
    return this.client.requestJson<Partial<WallTheme>>(`/wall/themes/${boardId}`, boardId, key);
  };

  updateWallTheme = (boardId: WallId, theme: WallTheme, key?: string | null) => {
    return this.client.requestJson<Partial<WallTheme>>(`/wall/themes/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify(theme),
    });
  };

  setAutoAlign = (boardId: WallId, autoAlign: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/autoAlign/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ autoAlign }),
    });
  };

  setAllowDrawingTools = (boardId: WallId, allowDrawingTools: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/allowDrawingTools/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ allowDrawingTools }),
    });
  };

  setAllowCardCreation = (boardId: WallId, allowCardCreation: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/allowCardCreation/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ allowCardCreation }),
    });
  };

  setAllowClustering = (boardId: WallId, allowClustering: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/allowClustering/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ allowClustering }),
    });
  };

  setAllowCardResizing = (boardId: WallId, allowCardResizing: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/allowCardResizing/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ allowCardResizing }),
    });
  };

  setAllowCardLocking = (boardId: WallId, allowCardLocking: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/allowCardLocking/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ allowCardLocking }),
    });
  };

  setAllowCardUnlocking = (boardId: WallId, allowCardUnlocking: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/allowCardUnlocking/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ allowCardUnlocking }),
    });
  };

  setAllowAiTools = (boardId: WallId, allowAiTools: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/allowAiTools/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ allowAiTools }),
    });
  };

  setShowCardOwnerNames = (boardId: WallId, showCardOwnerNames: boolean, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/showCardOwnerNames/${boardId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({ showCardOwnerNames }),
    });
  };
}
